export interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  department?: string;
  position?: string;
  work_experience?: string;
  region?: string;
  role: 'employee' | 'admin';
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

export interface Token {
  access_token: string;
  token_type: string;
  user: User;
}

export interface Test {
  id: number;
  title: string;
  description?: string;
  block: 'products' | 'vehicles';
  subgroup?: string | null;
  difficulty?: TestDifficulty | null;
  passing_score?: number;
  time_limit_minutes: number;
  is_active: boolean;
  is_archived?: boolean;
  created_at: string;
  questions_count?: number;
  question_texts?: string[];
  assignment_id?: number;
  assignment_status?: 'assigned' | 'started' | 'completed' | 'expired' | 'ASSIGNED' | 'STARTED' | 'COMPLETED' | 'EXPIRED';
  assignment_due_at?: string | null;
  assignment_completed_at?: string | null;
  assignment_is_active?: boolean;
  can_start?: boolean;
  can_open?: boolean;
  retake_authorized?: boolean;
  is_completed_by_user?: boolean;
  completed_attempt_id?: number | null;
  user_completed_at?: string | null;
  user_score?: number | null;
  user_is_passed?: boolean | null;
}

export type TestDifficulty = 'easy' | 'medium' | 'hard';

export interface AssignmentProgressIssue {
  field: string;
  code: 'missing_required_value' | 'invalid_boolean' | 'invalid_datetime' | 'inconsistent_completion' | 'completion_before_start' | 'future_timestamp' | 'invalid_score' | 'unexpected_unfinished_result' | 'invalid_time_limit' | 'deadline_overflow' | 'invalid_assignment_status' | 'missing_user' | 'multiple_unfinished_attempts';
}

export interface AssignmentProgressAttempt {
  attempt_id: number;
  assignment_id: number;
  user_id: number;
  test_id: number;
  started_at: string | null;
  completed_at: string | null;
  is_completed: boolean | null;
  is_passed: boolean | null;
  score: number | null;
  completed: boolean;
  malformed: boolean;
  outcome: 'passed' | 'failed' | 'unknown';
  state: 'completed_passed' | 'completed_failed' | 'completed_unknown' | 'in_progress' | 'in_progress_unknown' | 'abandoned';
  deadline_at: string | null;
  issues: AssignmentProgressIssue[];
}

export interface AssignmentProgressRow {
  assignment_id: number;
  user_id: number;
  test_id: number;
  full_name: string | null;
  assignment: {
    assigned_by: number;
    assigned_at: string | null;
    due_at: string | null;
    completed_at: string | null;
    is_active: boolean | null;
    is_required: boolean | null;
    status: 'assigned' | 'started' | 'completed' | 'expired' | null;
    inactive_or_expired: boolean;
  };
  status: 'not_started' | 'in_progress' | 'abandoned' | 'needs_retake' | 'passed' | 'unknown';
  activity: 'none' | 'in_progress' | 'in_progress_unknown' | 'abandoned';
  flags: { not_started: boolean; in_progress: boolean; abandoned: boolean; first_try_passed: boolean; first_try_failed: boolean; passed_after_retake: boolean; needs_retake: boolean; passed: boolean };
  first_completed_attempt_id: number | null;
  last_completed_attempt_id: number | null;
  counts: { attempts: number; completed: number; passed: number; failed: number; completed_unknown: number; unfinished: number; in_progress: number; in_progress_unknown: number; abandoned: number; malformed: number };
  attempts: AssignmentProgressAttempt[];
  issues: AssignmentProgressIssue[];
}

export interface AssignmentProgressResponse {
  test_id: number;
  test_title: string;
  time_limit_minutes: number | null;
  evaluated_at: string;
  issues: AssignmentProgressIssue[];
  summary: { assigned_total: number; first_try_passed: number; first_try_failed: number; passed_after_retake: number; needs_retake: number; not_started: number };
  rows: AssignmentProgressRow[];
}

export interface Answer {
  id: number;
  text: string;
  is_correct: boolean;
  order: number;
  matching_key?: string;
  image_url?: string;
}

export enum QuestionType {
  SINGLE_CHOICE = 'single_choice',
  MULTIPLE_CHOICE = 'multiple_choice',
  OPEN_ANSWER = 'open_answer',
  MATCHING = 'matching',
  IMAGE_IDENTIFICATION = 'image_identification'
}

export interface Question {
  id: number;
  text: string;
  type: QuestionType | 'single_choice' | 'multiple_choice' | 'open_answer' | 'matching' | 'image_identification';
  order: number;
  difficulty?: TestDifficulty | null;
  points: number;
  image_url?: string;
  explanation?: string | null;
  explanation_image_urls?: string[] | null;
  explanation_image_captions?: string[] | null;
  answers: Answer[];
}

export interface TestStartResponse {
  attempt_id: number;
  test_id: number;
  test_title: string;
  passing_score?: number;
  time_limit_minutes: number;
  questions: Question[];
  started_at: string;
  remaining_seconds: number;
  server_time?: string;
  answered: AnsweredQuestionState[];
}

export interface AnsweredQuestionState extends TestAnswer {
  is_correct: boolean;
  correct_answer_ids: number[];
  correct_answer_texts?: string[];
  correct_matching?: Record<string, string> | null;
  explanation?: string | null;
  explanation_image_urls?: string[] | null;
  explanation_image_captions?: string[] | null;
}

export interface AttemptProgressResponse {
  attempt_id: number;
  is_completed: boolean;
  remaining_seconds: number;
  server_time: string;
  answered: AnsweredQuestionState[];
}

export interface TestAnswer {
  question_id: number;
  answer_text?: string;
  selected_answer_ids?: number[];
  matching_data?: Record<string, string>;
  time_spent_seconds?: number;
}

export interface TestAttempt {
  id: number;
  test_id: number;
  test_title?: string;
  started_at: string;
  completed_at?: string;
  time_spent_seconds?: number;
  score?: number;
  is_passed?: boolean;
  is_completed: boolean;
  answers: TestAnswerDetail[];
}

export interface TestAnswerDetail {
  id: number;
  question_id: number;
  answer_text?: string;
  selected_answer_ids?: number[];
  matching_data?: Record<string, string>;
  is_correct?: boolean;
  time_spent_seconds?: number;
}

export interface VideoProgress {
  id?: number;
  video_id: number;
  last_position_seconds: number;
  duration_seconds: number;
  percent_watched: number;
  is_completed: boolean;
  first_viewed_at?: string | null;
  last_viewed_at?: string | null;
  completed_at?: string | null;
}

export interface TrainingVideo {
  id: number;
  folder_id: number;
  title: string;
  training_date?: string | null;
  description?: string | null;
  original_filename: string;
  file_revision?: string;
  mime_type: string;
  size_bytes: number;
  duration_seconds?: number | null;
  uploaded_at: string;
  sort_order: number;
  is_active: boolean;
  progress?: VideoProgress | null;
}

export interface VideoSubtitleTrack {
  language: 'ru' | 'en';
  label: string;
  url: string;
}

export interface TrainingFolder {
  id: number;
  title: string;
  description?: string | null;
  created_at: string;
  sort_order: number;
  is_active: boolean;
  video_count: number;
  progress_percent: number;
  completed_videos: number;
  videos: TrainingVideo[];
  document_count: number;
  completed_documents: number;
  documents: TrainingDocument[];
}

export interface DocumentProgress {
  document_id: number;
  first_viewed_at?: string | null;
  last_viewed_at?: string | null;
  is_completed: boolean;
  completed_at?: string | null;
}

export interface TrainingDocument {
  id: number;
  folder_id: number;
  title: string;
  training_date?: string | null;
  description?: string | null;
  document_kind: 'presentation' | 'transcript';
  original_filename: string;
  file_revision?: string;
  mime_type: string;
  size_bytes: number;
  uploaded_at: string;
  sort_order: number;
  is_active: boolean;
  progress?: DocumentProgress | null;
}

export interface VideoStatisticsRow {
  user_id: number;
  user_name: string;
  user_email: string;
  folder_id: number;
  folder_title: string;
  video_id: number;
  video_title: string;
  last_position_seconds: number;
  duration_seconds: number;
  percent_watched: number;
  is_completed: boolean;
  first_viewed_at: string;
  last_viewed_at: string;
  completed_at?: string | null;
}
